import React, { useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import listStyles from "./virtuallist.module.css";
import { escapeRegExp } from "../Tools";

export interface EventDataInfo {
  id?: string | number;
  code?: string;
  text?: string;
  label?: string;
  index?: number;
  disabled?: boolean;
  color?: string;
  padding?: string;
  Name?: string;
  Mask?: string;
  Color?: string;
  MultiCheckSet?: number;
  checkState?: string | number;
  objRef?: string;
}

interface VirtualListProps {
  items: Array<EventDataInfo>;
  estimateSize?: () => number;
  selected?: EventDataInfo;
  filterValue?: string;
  onClick?: (data?: EventDataInfo, e?: React.MouseEvent) => void;
  onDoubleClick?: (data?: EventDataInfo, e?: React.MouseEvent) => void;
  onKeyDown?: (data?: EventDataInfo, e?: React.KeyboardEvent<HTMLDivElement> | React.KeyboardEvent) => void;
  noFocus?: boolean;
  refList?: React.MutableRefObject<HTMLDivElement | null>;
  elementToFocusRef?: React.MutableRefObject<HTMLInputElement | null>;
}

//eslint-disable-next-line
const VirtualList = (props: VirtualListProps) => {
  const {
    items,
    selected,
    filterValue,
    estimateSize = () => 21,
    onClick,
    onDoubleClick,
    onKeyDown,
    noFocus,
    refList,
    elementToFocusRef,
  } = props;
  const isRenderEnd = useRef(true);

  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    //TODO Временное решение. Необходимо rowVirtualizer.scrollToIndex на выбранные элемент если он вне видимости
    focusSelectItem();
  }, [selected]);

  const focusSelectItem = () => {
    if (!items.length) return;
    const index = items.findIndex((value) => {
      return (
        (selected?.id !== undefined && value.id === selected?.id) ||
        (selected?.text !== undefined && value.text === selected?.text) ||
        (selected?.label !== undefined && value.label === selected?.label)
      );
    });

    if (index) rowVirtualizer.scrollToIndex(index);

    if (!noFocus) {
      setTimeout(() => {
        const indexToScroll = index === -1 ? 0 : index;
        const elementToFocus = parentRef.current?.querySelector<HTMLDivElement>(`[tabIndex="${indexToScroll}"]`);
        elementToFocus?.focus();
      }, 250);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, data: EventDataInfo) => {
    if (e.code === "Enter" && data.disabled) return;
    onKeyDown?.(data, e);
  };

  const handleOnClick = (e: React.MouseEvent, data: EventDataInfo) => {
    if (data.disabled) return;
    onClick?.(data, e);
  };

  const handleDbClick = (e: React.MouseEvent, data: EventDataInfo) => {
    if (data.disabled) return;
    onDoubleClick?.(data, e);
  };

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: estimateSize,
    overscan: 5,
    onChange: () => {
      if (isRenderEnd.current) {
        focusSelectItem();
        isRenderEnd.current = false;
      } else elementToFocusRef?.current?.focus();
    },
  });

  return (
    <div
      ref={parentRef}
      className={clsx(listStyles.VirtualListSmeta)}
      style={{
        height: `inherit`,
        overflow: "auto",
      }}
    >
      <div
        ref={refList}
        className={clsx(listStyles.VirtualListSmetaCointainer)}
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
        }}
        onKeyDown={(e) => {
          const target = e?.target as HTMLDivElement;
          const index = target?.tabIndex;
          if (index !== -1) handleKeyDown(e, items[index]);
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];
          const { id, text, label, disabled, color, padding } = item;
          const isSelected =
            (selected?.id !== undefined && id === selected?.id) ||
            (selected?.text !== undefined && text === selected?.text) ||
            (selected?.label !== undefined && label === selected?.label);

          const innerText = text || label;

          const matchData = filterValue && innerText?.toLowerCase()?.match(escapeRegExp(filterValue?.toLowerCase()));
          let highLight;
          if (matchData) {
            const startSubstring = matchData?.index || 0;
            const endSubstring = startSubstring + matchData?.[0]?.length || 0;
            const match = innerText?.slice(startSubstring, endSubstring);
            const first = innerText?.substring(0, startSubstring);
            const last = innerText?.substring(endSubstring);
            highLight = (
              <span>
                <span key={`letter-${first}-${id}-0`}>{first}</span>
                <span key={`bold-letter-${match}-${id}`} style={{ fontWeight: "bold" }}>
                  {match}
                </span>
                <span key={`letter-${last}-${id}-1`}>{last}</span>
              </span>
            );
          }

          return (
            <div
              className={clsx(
                listStyles.listItem,
                isSelected && listStyles.listItemSelected,
                disabled && listStyles.listDisabledItem,
              )}
              style={{ paddingLeft: padding, transform: `translateY(${virtualRow.start}px)` }}
              title={innerText}
              key={virtualRow.index}
              tabIndex={virtualRow.index}
              onClick={(e) => {
                handleOnClick(e, item);
              }}
              onDoubleClick={(e) => {
                handleDbClick(e, item);
              }}
            >
              {color && (
                <div
                  className={clsx(listStyles.listColorBox)}
                  style={{
                    backgroundColor: color,
                  }}
                ></div>
              )}
              <div
                style={{ height: `${estimateSize()}px`, display: "flex", alignItems: "center" }}
                className={clsx(listStyles.listItemLabel)}
              >
                {highLight || innerText}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VirtualList;
